import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import Layout from "../components/Layout";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { MagnifyingGlass, Plus, MapPin, Phone, CheckCircle } from "@phosphor-icons/react";

function ClientRow({ c }) {
    return (
        <Link
            to={`/clients/${c.id}`}
            data-testid={`client-row-${c.id}`}
            className="flex items-center gap-4 p-4 rounded-md hover:bg-cream transition-colors border border-transparent hover:border-sand"
        >
            <div className="w-10 h-10 rounded-full bg-sand grid place-items-center text-ink font-display font-bold">
                {c.first_name?.[0]}
                {c.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className="font-medium text-ink truncate">
                        {c.first_name} {c.last_name}
                    </p>
                    {c.is_fully_paid && (
                        <Badge className="bg-sage text-white hover:bg-sage-dark">
                            <CheckCircle size={12} className="mr-1" /> Soldé
                        </Badge>
                    )}
                </div>
                <div className="text-xs text-ink-soft flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1">
                        <MapPin size={12} /> {c.city}
                    </span>
                    <span className="flex items-center gap-1">
                        <Phone size={12} /> {c.phone}
                    </span>
                    <span>{c.purchase_type === "terrain_maison" ? `Terrain + Maison (${c.house_model})` : "Terrain"}</span>
                </div>
            </div>
            <div className="w-48 hidden sm:block">
                <div className="flex items-center justify-between text-xs text-ink-soft mb-1">
                    <span>{c.progress}%</span>
                    <span>{formatMoney(c.paid_amount)}</span>
                </div>
                <Progress
                    value={c.progress}
                    className={`h-2 ${c.is_fully_paid ? "[&>div]:bg-sage" : "[&>div]:bg-terracotta"}`}
                />
            </div>
        </Link>
    );
}

export default function Clients() {
    const [groups, setGroups] = useState({});
    const [search, setSearch] = useState("");

    async function load() {
        const { data } = await api.get("/clients/by-city");
        setGroups(data);
    }

    useEffect(() => {
        load();
    }, []);

    const cities = Object.keys(groups);
    const filter = (arr) =>
        arr.filter((c) => {
            const s = search.toLowerCase();
            if (!s) return true;
            return (
                `${c.first_name} ${c.last_name}`.toLowerCase().includes(s) ||
                c.phone?.toLowerCase().includes(s) ||
                c.email?.toLowerCase().includes(s)
            );
        });

    return (
        <Layout>
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-2">Clients</p>
                    <h1 className="font-display text-4xl font-bold text-ink">Triés par cité</h1>
                </div>
                <Link
                    to="/clients/new"
                    data-testid="new-client-button"
                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-5 py-3 rounded-md font-medium active:translate-y-[1px] transition-all"
                >
                    <Plus size={18} weight="bold" /> Nouveau client
                </Link>
            </div>

            <div className="bento-card mb-6">
                <div className="relative">
                    <MagnifyingGlass
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft"
                    />
                    <Input
                        placeholder="Rechercher un client (nom, téléphone, email)…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 bg-cream"
                        data-testid="client-search-input"
                    />
                </div>
            </div>

            {cities.length === 0 ? (
                <div className="bento-card text-center py-12">
                    <p className="text-ink-soft">Aucun client. Commencez par en ajouter un.</p>
                </div>
            ) : (
                <Tabs defaultValue={cities[0]} className="w-full">
                    <TabsList className="bg-white border border-sand rounded-md p-1 flex flex-wrap h-auto">
                        {cities.map((c) => (
                            <TabsTrigger
                                key={c}
                                value={c}
                                data-testid={`tab-city-${c}`}
                                className="data-[state=active]:bg-terracotta data-[state=active]:text-white px-4 py-2 rounded text-sm"
                            >
                                <MapPin size={14} className="mr-1.5" />
                                {c}
                                <span className="ml-2 text-xs opacity-70">({groups[c].length})</span>
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {cities.map((c) => (
                        <TabsContent key={c} value={c} className="mt-6">
                            <div className="bento-card divide-y divide-sand/60 !p-2">
                                {filter(groups[c]).map((cl) => (
                                    <ClientRow key={cl.id} c={cl} />
                                ))}
                                {filter(groups[c]).length === 0 && (
                                    <p className="p-6 text-sm text-ink-soft">Aucun résultat.</p>
                                )}
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>
            )}
        </Layout>
    );
}
